import "./fonts";
import "./styles.css";
import { ApiError, createApi, loadClientConfig } from "./api";
import wordmark from "./assets/valphira-logo.svg";
import { createAuth } from "./auth";
import { bindBell, bindUserMenu, renderHeader, renderUser } from "./header";
import { loadAll, loadChangeRequests, loadCompanies, loadCompany, loadLog, loadTasks, S, setRenderer, VIEWS, type View } from "./state";
import { $, esc, isTyping } from "./util";
import { bindAnfragen, renderAnfragen } from "./views/anfragen";
import { bindChangeRequests, renderAntraege } from "./views/antraege";
import { fitBoard, renderBoard } from "./views/board";
import { renderBriefing } from "./views/briefing";
import { bindDrawer, closeDrawer, openDeal, renderDrawer } from "./views/drawer";
import { bindFirma, renderFirma, resetFirmaEdit } from "./views/firma";
import { renderFirmen } from "./views/firmen";
import { renderKlaer } from "./views/klaer";
import { bindSearch, filterOpenView } from "./views/search";
import { bindClaude, renderClaude, showClaude } from "./views/claude";
import { bindMailCards } from "./views/mailcard";
import { bindMerge } from "./views/merge";
import { bindSettings, loadSettings, renderSettings } from "./views/settings";
import { bindProtokoll, renderProtokoll } from "./views/protokoll";
import { bindTasks, renderAufgaben } from "./views/tasks";
import { bindVolumen, renderVolumen } from "./views/volumen";

/** Spec 10: polling every 60 s on open views, no WebSockets in v1. */
const POLL_MS = 60_000;

function emptyState(title: string, text: string) {
  return `<div class="empty"><b>${esc(title)}</b>${esc(text)}</div>`;
}

/* ---------- gate: login problems and missing access ---------- */
function showGate(title: string, text: string, actions: { label: string; primary?: boolean; run: () => void }[] = []) {
  document.querySelectorAll<HTMLElement>("[data-v]").forEach((s) => (s.hidden = true));
  document.querySelectorAll<HTMLElement>(".top .wrap > :not(.brand)").forEach((el) => (el.hidden = true));
  const gate = $("#v-gate");
  gate.hidden = false;
  gate.innerHTML = `<img class="logo gate-logo" src="${wordmark}" alt="Valphira">${emptyState(title, text)}<div class="actions">${actions
    .map((a, i) => `<button class="btn${a.primary ? " primary" : ""}" data-gate="${i}">${esc(a.label)}</button>`)
    .join("")}</div>`;
  gate.onclick = (e) => {
    const i = (e.target as HTMLElement).closest<HTMLElement>("[data-gate]")?.dataset.gate;
    if (i !== undefined) actions[Number(i)]!.run();
  };
}

/* ---------- routing ---------- */
function route() {
  // #zuordnen is the name users see; #klaerfaelle stays for the links in older mails.
  const raw = location.hash.slice(1);
  const h = raw === "zuordnen" ? "klaerfaelle" : raw;
  if (h.startsWith("deal-")) {
    void openDeal(h.slice(5));
  } else {
    if (h === "claude" || h.startsWith("claude-")) {
      S.view = "claude";
      showClaude(h.startsWith("claude-") ? h.slice(7) : null);
    } else if (h.startsWith("firma-")) {
      S.view = "firma";
      if (S.companyId !== h.slice(6)) {
        S.companyId = h.slice(6);
        S.company = null;
        resetFirmaEdit();
      }
      void loadCompany()
        .catch(() => undefined)
        .then(() => render());
    } else if ((VIEWS as readonly string[]).includes(h)) S.view = h as View;
    // The header search filters the board and the company list: they show what the field says.
    const refiltered = filterOpenView($<HTMLInputElement>("#gs-input").value) === "firmen";
    if (S.view === "firmen" && (!S.companies || refiltered)) void loadCompanies().then(() => render(), () => undefined);
    if (S.view === "antraege") void loadChangeRequests().then(() => render(), () => undefined);
    if (S.view === "aufgaben") void loadTasks().then(() => render(), () => undefined);
    if (S.view === "protokoll") void loadLog().then(() => render(), () => undefined);
    if (S.openId) closeDrawer(false);
  }
  document.querySelectorAll<HTMLElement>("[data-v]").forEach((s) => (s.hidden = s.dataset.v !== S.view));
  const tab = S.view === "firma" ? "firmen" : S.view;
  document.querySelectorAll<HTMLAnchorElement>(".top a[data-view]").forEach((a) => {
    if (a.dataset.view === tab) a.setAttribute("aria-current", "page");
    else a.removeAttribute("aria-current");
  });
  render();
}

/* ---------- render ---------- */
function render() {
  renderHeader();
  if (S.view === "briefing") renderBriefing();
  if (S.view === "anfragen") renderAnfragen();
  if (S.view === "board") renderBoard();
  if (S.view === "volumen") renderVolumen();
  if (S.view === "klaerfaelle") renderKlaer();
  if (S.view === "firmen") renderFirmen();
  if (S.view === "firma") renderFirma();
  if (S.view === "claude") renderClaude();
  if (S.view === "einstellungen") renderSettings();
  if (S.view === "antraege") renderAntraege();
  if (S.view === "aufgaben") renderAufgaben();
  if (S.view === "protokoll") renderProtokoll();
  if (S.openId) renderDrawer();
}

async function refresh() {
  if (document.hidden) return;
  try {
    await loadAll();
    if (S.view === "einstellungen") await loadSettings();
    // The poll never redraws under the user's hands; the next action shows the new data.
    if (isTyping()) return;
    render();
  } catch {
    // The next poll tries again; a failed write shows its own message.
  }
}

/* ---------- boot ---------- */
async function boot() {
  const cfg = await loadClientConfig();
  if (cfg.env !== "prod") {
    const tag = $("#envtag");
    tag.textContent = cfg.env;
    tag.hidden = false;
  }

  S.auth = await createAuth(cfg);
  if (!(await S.auth.ensureLogin())) return; // on the way to the Microsoft login page

  S.api = createApi(() => S.auth.getToken());
  try {
    S.me = await S.api.me();
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      showGate("Kein Zugriff", err.message, [{ label: "Abmelden", run: () => void S.auth.logout() }]);
      return;
    }
    throw err;
  }
  S.readOnly = !S.me.canWrite;
  setRenderer(render);
  renderUser();
  bindUserMenu();
  bindBell();
  $("#tabs").hidden = false;
  $("#tools").hidden = false;
  bindDrawer();
  bindFirma();
  bindMerge();
  bindChangeRequests();
  bindTasks();
  bindProtokoll();
  bindVolumen();
  bindAnfragen();
  bindMailCards();
  bindSettings();
  bindSearch();
  bindClaude();
  addEventListener("hashchange", route);
  addEventListener("resize", () => S.view === "board" && fitBoard());
  route();
  await loadAll();
  route();
  setInterval(() => void refresh(), POLL_MS);
  document.addEventListener("visibilitychange", () => void refresh());
}

boot().catch((err: unknown) => {
  console.error(err);
  // Entra refuses users without an assigned app role ("Zuweisung erforderlich").
  if (String((err as Error)?.message ?? err).includes("AADSTS50105")) {
    showGate("Kein Zugriff", "Dein Konto ist für die Valphira Pipeline nicht freigeschaltet. Bitte bei der Administration anfragen.", [
      { label: "Neu laden", run: () => location.reload() },
    ]);
    return;
  }
  // No answer at all (network) or 502–504: usually the minute in which a new version starts.
  const unreachable = err instanceof TypeError || (err instanceof ApiError && err.status >= 502 && err.status <= 504);
  if (unreachable) {
    showGate("Gleich wieder da", "Die Pipeline ist gerade nicht erreichbar, meist weil eine neue Version startet. Sie lädt in 20 Sekunden von selbst neu.", [
      { label: "Jetzt neu laden", primary: true, run: () => location.reload() },
    ]);
    setTimeout(() => location.reload(), 20_000);
    return;
  }
  const text = err instanceof ApiError ? err.message : `Die Anmeldung oder der Start der Anwendung ist fehlgeschlagen (${String((err as Error)?.message ?? err).slice(0, 200)}).`;
  showGate("Das hat nicht geklappt", text, [
    { label: "Neu laden", primary: true, run: () => location.reload() },
    // A stuck login: forget the stored Microsoft session of this app and sign in afresh.
    {
      label: "Neu anmelden",
      run: () => {
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch {
          // Storage blocked: the reload still starts a fresh login.
        }
        location.reload();
      },
    },
  ]);
});
